import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import {
  LayoutDashboard, Users, CreditCard, ScrollText, Cpu, LogOut, Menu, X, MessageCircle,
  Package, ShoppingCart, BarChart3, UserCog, Shield, Sun, Moon, Monitor, Wallet, Settings, KeyRound,
} from "lucide-react";
import { useState } from "react";
import Suspended from "@/pages/Suspended";
import BrandLogo from "@/components/BrandLogo";
import ChangePasswordModal from "@/components/ChangePasswordModal";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/members", label: "Socios", icon: Users, perm: "members:view" },
  { to: "/plans", label: "Planes", icon: CreditCard, perm: "plans:view" },
  { to: "/products", label: "Productos", icon: Package, perm: "products:view" },
  { to: "/pos", label: "Cobro", icon: ShoppingCart, perm: "cobros:view" },
  { to: "/caja", label: "Caja", icon: Wallet, perm: "cash:view" },
  { to: "/reports", label: "Reportes", icon: BarChart3, perm: "reports:view" },
  { to: "/access-logs", label: "Accesos", icon: ScrollText, perm: "access:view" },
  { to: "/notifications", label: "Avisos", icon: MessageCircle, perm: "notifications:view" },
  { to: "/devices", label: "Dispositivos ZK", icon: Cpu, perm: "devices:view" },
  { to: "/employees", label: "Empleados", icon: UserCog, perm: "users:view" },
  { to: "/ajustes", label: "Ajustes", icon: Settings, perm: "branding:edit" },
];

const roleLabel = (u) => u?.platform_role === "glg_admin" ? "Administrador GLG" : (u?.role === "owner" ? "Dueño" : "Empleado");

export default function AppShell({ children }) {
  const { user, logout, can, isGlg, gymInfo } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const doLogout = async () => { await logout(); navigate("/login"); };
  const suspended = !isGlg && gymInfo?.subscription_status === "suspended";

  const themes = [["dark", Moon], ["light", Sun], ["system", Monitor]];

  const SidebarInner = () => (
    <>
      <div className="px-6 py-6 border-b border-white/10">
        <BrandLogo withWordmark size="h-10" />
      </div>
      <nav className="flex-1 py-4 overflow-y-auto">
        {isGlg && (
          <NavLink to="/glg" onClick={() => setOpen(false)} data-testid="nav-glg"
            className={({ isActive }) => `flex items-center gap-3 px-6 py-3 text-sm font-semibold border-l-2 mb-2 ${isActive ? "border-[#FFD60A] bg-[#FFD60A]/10 text-[#FFD60A]" : "border-transparent text-[#FFD60A]/80 hover:bg-white/5"}`}>
            <Shield className="w-4 h-4" /> Administración GLG
          </NavLink>
        )}
        {nav.filter((n) => !n.perm || can(n.perm)).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 px-6 py-3 text-sm font-semibold transition-colors border-l-2 ${isActive ? "border-[#00f0ff] bg-[#00f0ff]/5 text-white" : "border-transparent text-white/50 hover:text-white hover:bg-white/5"}`}>
            <n.icon className="w-4 h-4" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-4">
        <div className="flex items-center gap-1 mb-3 bg-white/5 p-1" data-testid="theme-toggle">
          {themes.map(([t, Icon]) => (
            <button key={t} data-testid={`theme-${t}`} onClick={() => setTheme(t)}
              className={`flex-1 flex items-center justify-center py-1.5 ${theme === t ? "bg-[#00f0ff] text-black" : "text-white/50 hover:text-white"}`}>
              <Icon className="w-4 h-4" />
            </button>
          ))}
        </div>
        <p className="text-xs text-white/40">{roleLabel(user)}</p>
        <p className="text-sm font-semibold truncate">{user?.email}</p>
        {gymInfo?.gym && !isGlg && <p className="text-xs text-[#00f0ff] truncate">{gymInfo.gym.name}</p>}
        <button data-testid="change-password-button" onClick={() => setShowPwd(true)} className="mt-3 w-full flex items-center justify-center gap-2 border border-white/20 py-2 text-sm hover:bg-white/5 transition-colors">
          <KeyRound className="w-4 h-4" /> Mi perfil
        </button>
        <button data-testid="logout-button" onClick={doLogout} className="mt-2 w-full flex items-center justify-center gap-2 border border-white/20 py-2 text-sm hover:bg-white/5 transition-colors">
          <LogOut className="w-4 h-4" /> Salir
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-[#050505] text-white">
      <aside className="hidden lg:flex w-64 flex-col fixed h-screen bg-[#0a0a0a] border-r border-white/10 z-40">
        <SidebarInner />
      </aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <aside className="w-64 flex flex-col bg-[#0a0a0a] border-r border-white/10"><SidebarInner /></aside>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
        </div>
      )}
      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 bg-[#0a0a0a] border-b border-white/10 flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2"><BrandLogo size="h-8" /><span className="font-display text-xl tracking-wide">GLG GYM CONTROL</span></div>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)}>{open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
        </header>
        <main className="p-4 sm:p-6 lg:p-8">{suspended ? <Suspended /> : children}</main>
      </div>
      <ChangePasswordModal open={showPwd} onClose={() => setShowPwd(false)} />
    </div>
  );
}
